import { useMemo, useState } from 'react';
import { CheckCircle2, Circle, Lock, Plus, ClipboardCheck } from 'lucide-react';
import {
  createReview,
  reviewProgress,
  tallyUsefulness,
  STAND_DOWN_CHECKLIST,
  INSIGHT_RATING_LABELS,
  type Incident,
  type PostIncidentReview,
  type InsightRating,
  type AccuracyVerdict,
  type LocalNoteDraft,
} from '@duty/shared';
import { useIncidentStore } from '@/incident/store';
import { useRole } from '@/auth/role';
import { Segmented, TextInput } from '@/components/ui/controls';

const VERDICTS: { value: AccuracyVerdict; label: string }[] = [
  { value: 'accurate', label: 'Accurate' },
  { value: 'inaccurate', label: 'Inaccurate' },
  { value: 'not-checked', label: 'Not checked' },
];

const RATINGS: { value: InsightRating; label: string }[] = (
  ['useful', 'not-useful', 'noise'] as InsightRating[]
).map((value) => ({ value, label: INSIGHT_RATING_LABELS[value] }));

const NOTE_CATS: LocalNoteDraft['category'][] = ['access', 'water', 'hazard', 'exposure', 'other'];

/**
 * Post-incident review + stand-down. The reviewer works the checklist, confirms
 * whether the seeded spatial facts matched reality, rates how useful each insight
 * was, and can capture new local knowledge. Nothing here changes the past
 * incident — it produces a structured record (and audit events) for an SME to
 * act on. Gated by the `review.conduct` capability.
 */
export function ReviewPanel({ incident }: { incident: Incident }) {
  const { insights, logEvent } = useIncidentStore();
  const { can } = useRole();
  const [review, setReview] = useState<PostIncidentReview>(() => createReview(incident, insights));
  const [noteText, setNoteText] = useState('');
  const [noteCat, setNoteCat] = useState<LocalNoteDraft['category']>('access');

  const progress = useMemo(() => reviewProgress(review), [review]);
  const tally = useMemo(() => tallyUsefulness(review), [review]);

  if (!can('review.conduct')) {
    return (
      <p className="inline-flex items-center gap-1.5 text-xs text-muted">
        <Lock size={13} /> The post-incident review is conducted by a reviewer / SME or admin.
      </p>
    );
  }

  const toggleCheck = (id: string) =>
    setReview((r) => {
      const next = { ...r.checklist, [id]: !r.checklist[id] };
      logEvent('standdown.checked', { item: id, checked: next[id] });
      return { ...r, checklist: next };
    });

  const setVerdict = (key: 'hydrant' | 'access', v: AccuracyVerdict) =>
    setReview((r) => ({ ...r, spatial: { ...r.spatial, [key]: v } }));

  const rate = (insightId: string, rating: InsightRating) =>
    setReview((r) => {
      logEvent('review.feedback', { insightId, rating });
      return {
        ...r,
        insightFeedback: r.insightFeedback.map((f) =>
          f.insightId === insightId ? { ...f, rating } : f,
        ),
      };
    });

  const addNote = () => {
    const text = noteText.trim();
    if (!text) return;
    const note: LocalNoteDraft = { id: `note_${Date.now().toString(36)}`, category: noteCat, text };
    setReview((r) => ({ ...r, localNotes: [...r.localNotes, note] }));
    logEvent('localnote.created', { category: note.category, text: note.text });
    setNoteText('');
  };

  const complete = () =>
    setReview((r) => {
      const completedAt = new Date().toISOString();
      logEvent('review.completed', {
        usefulness: tally,
        spatial: r.spatial,
        localNotes: r.localNotes.length,
      });
      return { ...r, completedAt };
    });

  return (
    <div className="space-y-4">
      {/* Stand-down checklist */}
      <div>
        <SubHead>
          Stand-down checklist
          <Count done={progress.checklistDone} total={progress.checklistTotal} />
        </SubHead>
        <ul className="space-y-1">
          {STAND_DOWN_CHECKLIST.map((c) => {
            const done = !!review.checklist[c.id];
            return (
              <li key={c.id}>
                <button
                  onClick={() => toggleCheck(c.id)}
                  aria-pressed={done}
                  className="flex w-full items-start gap-2 text-left text-sm text-text/90 hover:text-accent"
                >
                  {done ? (
                    <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-ok" />
                  ) : (
                    <Circle size={15} className="mt-0.5 shrink-0 text-muted" />
                  )}
                  <span className={done ? 'text-muted line-through' : ''}>{c.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Spatial accuracy */}
      <div className="border-t border-border pt-3">
        <SubHead>Local data accuracy</SubHead>
        <div className="space-y-2">
          <Verdict label="Nearest hydrant" value={review.spatial.hydrant} onChange={(v) => setVerdict('hydrant', v)} />
          <Verdict label="Access / local notes" value={review.spatial.access} onChange={(v) => setVerdict('access', v)} />
        </div>
      </div>

      {/* Insight usefulness feedback */}
      <div className="border-t border-border pt-3">
        <SubHead>
          Insight usefulness
          <Count done={progress.insightsRated} total={progress.insightsTotal} />
        </SubHead>
        {review.insightFeedback.length === 0 ? (
          <p className="text-xs text-muted">No insights were shown during this incident.</p>
        ) : (
          <ul className="space-y-2">
            {review.insightFeedback.map((f) => (
              <li key={f.insightId} className="space-y-1">
                <span className="block text-sm text-text/90">{f.title}</span>
                <Segmented options={RATINGS} value={f.rating} onChange={(v) => v && rate(f.insightId, v)} />
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* New local knowledge */}
      <div className="border-t border-border pt-3">
        <SubHead>Capture local knowledge</SubHead>
        <div className="space-y-2">
          <Segmented
            options={NOTE_CATS.map((c) => ({ value: c, label: c[0]!.toUpperCase() + c.slice(1) }))}
            value={noteCat}
            onChange={(v) => v && setNoteCat(v)}
          />
          <div className="flex items-center gap-2">
            <TextInput value={noteText} onChange={setNoteText} placeholder="e.g. rear lane access via Gibraltar St" ariaLabel="New local note" />
            <button
              onClick={addNote}
              disabled={!noteText.trim()}
              className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border bg-surface-2 px-2 py-1.5 text-xs text-muted hover:text-accent disabled:opacity-40"
            >
              <Plus size={13} /> Add
            </button>
          </div>
          {review.localNotes.length > 0 && (
            <ul className="space-y-1 text-xs text-muted">
              {review.localNotes.map((n) => (
                <li key={n.id}>
                  <span className="chip mr-1">{n.category}</span>
                  {n.text}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Completion */}
      <div className="border-t border-border pt-3">
        <div className="mb-2 flex flex-wrap gap-2 text-[11px] text-muted">
          <Pill>Useful {tally.useful}</Pill>
          <Pill>Not useful {tally['not-useful']}</Pill>
          <Pill>Noise {tally.noise}</Pill>
          {tally.unrated > 0 && <Pill>Unrated {tally.unrated}</Pill>}
        </div>
        {review.completedAt ? (
          <p className="inline-flex items-center gap-1.5 text-xs text-ok">
            <ClipboardCheck size={14} /> Review recorded. These notes are advisory inputs for SME review.
          </p>
        ) : (
          <button
            onClick={complete}
            disabled={!progress.ready}
            className="inline-flex items-center gap-1.5 rounded-md border border-accent/60 bg-accent/15 px-3 py-1.5 text-xs text-accent hover:bg-accent/25 disabled:cursor-not-allowed disabled:opacity-40"
            title={progress.ready ? '' : 'Complete the checklist and rate every insight first'}
          >
            <ClipboardCheck size={14} /> Complete review
          </button>
        )}
      </div>
    </div>
  );
}

function SubHead({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mb-2 flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-muted">
      <span className="inline-flex items-center gap-2">{children}</span>
    </h3>
  );
}

function Count({ done, total }: { done: number; total: number }) {
  return (
    <span className="rounded-full bg-surface-2 px-1.5 text-[10px] normal-case text-muted">
      {done}/{total}
    </span>
  );
}

function Pill({ children }: { children: React.ReactNode }) {
  return <span className="rounded-full bg-surface-2 px-2 py-0.5">{children}</span>;
}

function Verdict({
  label,
  value,
  onChange,
}: {
  label: string;
  value: AccuracyVerdict;
  onChange: (v: AccuracyVerdict) => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span className="text-sm text-text/90">{label}</span>
      <Segmented options={VERDICTS} value={value} onChange={(v) => v && onChange(v)} />
    </div>
  );
}
